import { useNavigate, useSearchParams } from "react-router";
import { useCallback } from "react";

type QueryParams = Record<string, string | number | boolean | null | undefined>;

interface NavigateWithQueryOptions {
  /** Replace the current history entry instead of pushing a new one */
  replace?: boolean;
  /** Discard all existing query params and use only the ones provided */
  resetParams?: boolean;
}

/**
 * Returns a `navigateTo` function that navigates to a path while
 * merging, adding, or resetting query parameters.
 *
 * @example
 * const navigateTo = useQueryNav();
 *
 * // Preserve existing params and add/overwrite some
 * navigateTo("/search", { q: "hello", page: 1 });
 *
 * // Remove a param by setting it to null or undefined
 * navigateTo("/search", { page: null });
 *
 * // Discard all existing params, start fresh
 * navigateTo("/search", { q: "hello" }, { resetParams: true });
 *
 * // Stay on the current path, just update params
 * navigateTo(null, { sort: "asc" });
 */
export function useQueryNav() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const navigateTo = useCallback(
    (
      path: string | null,
      params?: QueryParams,
      options: NavigateWithQueryOptions = {},
    ) => {
      const { replace = false, resetParams = false } = options;

      // Start from existing params, or a blank slate
      const next = resetParams
        ? new URLSearchParams()
        : new URLSearchParams(searchParams);

      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value === null || value === undefined) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        }
      }

      const queryString = next.toString();
      const currentPath = window.location.pathname;
      const targetPath = path ?? currentPath;
      const fullPath = queryString
        ? `${targetPath}?${queryString}`
        : targetPath;

      navigate(fullPath, { replace });
    },
    [navigate, searchParams],
  );

  return navigateTo;
}

/**
 * Returns a `buildUrl` function — same logic as navigateTo,
 * but just returns the URL string instead of navigating.
 * Useful for NavLink `to` props.
 */
export function useQueryUrl() {
  const [searchParams] = useSearchParams();

  const buildUrl = useCallback(
    (
      path: string,
      params?: QueryParams,
      options: NavigateWithQueryOptions = {},
    ) => {
      const { resetParams = false } = options;

      const next = resetParams
        ? new URLSearchParams()
        : new URLSearchParams(searchParams);

      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value === null || value === undefined) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        }
      }

      const queryString = next.toString();
      return queryString ? `${path}?${queryString}` : path;
    },
    [searchParams],
  );

  return buildUrl;
}
